/**
 * AccountLayout.tsx
 * Minimal layout used for the profile page and other account-level screens.
 * Renders a simple top header bar with the Kabbi logo and a user avatar dropdown
 * (profile link + logout), followed by a centered narrow content area.
 *
 * An info banner at the top of the content area informs the user that some
 * features are mobile-only (i.e. the driver/passenger app).
 */

import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, Info, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/store/authStore.ts';

export default function AccountLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  /** Logs the user out and redirects to the login page. */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Thin top header with logo and user dropdown */}
      <header className="h-14 border-b flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-2">
          <div className="bg-primary rounded-lg p-1.5">
            <Car className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg">Kabbi</span>
        </div>

        {/* User avatar dropdown — profile and logout */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-xs">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              {/* User name hidden on small screens to save space */}
              <span className="hidden sm:block text-sm font-medium leading-none">{user?.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              {t('nav.profile')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              {t('common.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Narrow centered content area with info banner */}
      <main className="flex-1 flex justify-center p-4 md:p-6">
        <div className="w-full max-w-lg">
          {/* Banner informing users that driver/passenger features are mobile-only */}
          <Alert className="mb-6">
            <Info className="h-4 w-4" />
            <AlertDescription>{t('profile.mobileOnlyBanner')}</AlertDescription>
          </Alert>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
